import setVehicleFromGetPageDetails from "action/garage/setVehicleFromGetPageDetails";
import { IDetailedProductResponse } from "api/interfaces/get-page-details/product-page.type";
import axios from "fetch-api/fetch-api";
import usePrevious from "hoc/usePrevious";
import { RootState } from "interfaces/generics.type";
import { useRouter } from "next/router";
import qs from "query-string";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PASentry from "util/pa-sentry";
import { getPageDetails } from "util/url/apiConstants";
import filterUrl from "util/url/filterUrl";
import getCustomSizeQuery from "util/url/getCustomSizeQuery";
import { getHrefAndPath } from "util/url/getHrefAndPath";
import { b2bUrl } from "util/url/static-urls";
import { API_PARAM_ORDERS } from "util/url/urlHelper";
import { createVehicleDescription } from "util/vehicle/createVehicleDescription";
import { equalVehicles } from "util/vehicle/equal-vehicles";
import { getFitmentOptionQS } from "util/vehicle/get-fitment-option-q-s";
import { getFitmentOptionObj } from "util/vehicle/getFitmentOptionObj";

let RAN_FIRST_TIME = false;

function withUpdatedPriceInventory(WrappedComponent: any) {
  function HOC(props: any) {
    const [actualResponse, setActualResponse] = useState<IDetailedProductResponse>();
    const [isMounted, setIsMounted] = useState(false);
    const [clientSideProductApiResReady, setClientSideProductApiResReady] = useState(false);
    const { isLoading: isGarageLoading, selectedVehicle } = useSelector((store: RootState) => store.garage);
    const vehicleDescription = createVehicleDescription(selectedVehicle, true, false, true);
    const { businessCustomer } = useSelector((store: RootState) => store.authenticationState);
    const { fitmentOptions, engine, ...vehicleWithoutFitment } = selectedVehicle || {};
    const router = useRouter();
    const dispatch = useDispatch();
    const prevSelectedVehicle = usePrevious(selectedVehicle);

    // const { make, model, year } = vehicleWithoutFitment || {};
    const fitmentOptionQs = getFitmentOptionQS(fitmentOptions);
    let canonicalUrl: any;
    const { product } = props.actualResponse || props.product || {};
    if (product?.canonicalUrl) {
      canonicalUrl = product.canonicalUrl;
    } else if (!process.browser) {
      PASentry.logException(new Error("canonicalUrl not exist on product", null, { props }));
    }

    function getUpdatedResponse() {
      const windowURL = window?.location?.pathname?.substring(1);
      let pageUrl = windowURL;
      if (windowURL.indexOf("part/") >= 0) {
        pageUrl = canonicalUrl;
      } else if (windowURL.indexOf("fr/") >= 0) {
        pageUrl = canonicalUrl;
      }
      const fitmentOptionObj = getFitmentOptionObj(fitmentOptions);
      const customSizes = getCustomSizeQuery();
      const queryObj = {
        url: pageUrl,
        ...vehicleWithoutFitment,
        ...fitmentOptionObj,
        ...customSizes,
      };

      const requestUrl = `${getPageDetails}?${qs.stringify(queryObj, {
        sort: (a: any, b: any) => API_PARAM_ORDERS.indexOf(b) - API_PARAM_ORDERS.indexOf(a),
      })}`;

      // If Server Side Request is same as Client side do not call API
      if (filterUrl(props.serverRequestUrl, ["numberOfProducts"]) === requestUrl && !isMounted) {
        setData(props);
        return;
      }

      axios.get(requestUrl)
        .then(({
          data,
        }: any) => {
          setData(data);
        });
    }

    function setData(data: any) {
      RAN_FIRST_TIME = true;
      setIsMounted(true);

      if (data && data.actualResponse) {
        const { redirectUrl, vehicleSelectionOptions } = data.actualResponse;
        dispatch(setVehicleFromGetPageDetails(vehicleSelectionOptions));
        if (redirectUrl) {
          router.replace(redirectUrl);
        } else {
          setActualResponse(data.actualResponse);
          setClientSideProductApiResReady(true);
        }
      }
    }

    // this have to be done only for SSR render page, not for subsqunt client side render pages.
    useEffect(() => {
      // Garage loading check is required here bcz refreshState() sets postal code in redux.
      // When user-session data is set in redux, we are safe to make the API call
      if (RAN_FIRST_TIME || isGarageLoading) {
        return;
      }

      getUpdatedResponse();
    }, [isGarageLoading]);

    // set isMounted if CSR is already done
    useEffect(() => {
      if (RAN_FIRST_TIME) {
        setIsMounted(true);
      }
      if (isMounted && businessCustomer && !equalVehicles(prevSelectedVehicle, selectedVehicle)) {
        const { href, asPath } = getHrefAndPath({ asPath: b2bUrl });
        router.push(href, asPath);
        return;
      }
      if (isMounted) {
        getUpdatedResponse();
      }
    }, [vehicleDescription, fitmentOptionQs]);

    useEffect(() => {
      if (actualResponse?.product && actualResponse.product.canonicalUrl !== canonicalUrl) {
        setActualResponse(undefined);
      }
    }, [canonicalUrl]);

    const propsToPass = actualResponse ? { actualResponse, RAN_FIRST_TIME, clientSideProductApiResReady } : {};

    return (
      <WrappedComponent
        {...props}
        {...propsToPass}
      />
    );
  }
  return HOC;
}

export default withUpdatedPriceInventory;
